import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Shield,
  Calendar,
  Save,
  X,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Input } from '@components/forms/Input';
import { Select } from '@components/forms/Select';
import { Button } from '@components/common/Button';
import { PageHeader } from '@components/common/PageHeader';
import { getCurrentApplicant } from '@mocks/applicants';

const profileSchema = z.object({
  first_name: z.string().min(2, 'First name is required'),
  middle_name: z.string().optional(),
  last_name: z.string().min(2, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  address_line1: z.string().min(5, 'Address is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  country: z.string().min(2, 'Country is required'),
  employer: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const Profile: React.FC = () => {
  const profile = getCurrentApplicant();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: profile.first_name,
      middle_name: profile.middle_name || '',
      last_name: profile.last_name,
      email: profile.email || '',
      phone: profile.phone || '',
      address_line1: profile.address_line1 || '',
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || '',
      employer: profile.employer || '',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    
    // Mock API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    console.log('✅ Mock: Profile updated', data);
    toast.success('Profile updated successfully!');
    
    setIsLoading(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="My Profile"
        description="Manage your personal information and account details"
        actions={
          !isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Summary */}
        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <div className="text-center">
              <div className="w-24 h-24 bg-ncaa-primary text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                {profile.first_name[0]}{profile.last_name[0]}
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {profile.first_name} {profile.middle_name} {profile.last_name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">{profile.email}</p>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Badge 
                  variant={profile.nin_status === 'VERIFIED' ? 'success' : 'warning'}
                  icon={<Shield className="h-4 w-4" />}
                >
                  NIN {profile.nin_status}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Account Info Card */}
          <Card title="Account Information" icon={<User className="h-5 w-5" />}>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500">NIN</p>
                <p className="font-medium text-gray-900">{profile.nin}</p>
              </div>
              <div>
                <p className="text-gray-500">Date of Birth</p>
                <p className="font-medium text-gray-900">{formatDate(profile.dob)}</p>
              </div>
              <div>
                <p className="text-gray-500">Sex</p>
                <p className="font-medium text-gray-900">{profile.sex === 'M' ? 'Male' : 'Female'}</p>
              </div>
              <div>
                <p className="text-gray-500">Nationality</p>
                <p className="font-medium text-gray-900">{profile.nationality}</p>
              </div>
              <div>
                <p className="text-gray-500">Member Since</p>
                <p className="font-medium text-gray-900">{formatDate(profile.created_at)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column - Editable Form */}
        <div className="lg:col-span-2">
          <Card
            title={isEditing ? 'Edit Profile' : 'Profile Details'}
            icon={<User className="h-5 w-5" />}
            actions={
              isEditing && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                    icon={<X className="h-4 w-4" />}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    loading={isLoading}
                    icon={<Save className="h-4 w-4" />}
                  >
                    Save Changes
                  </Button>
                </div>
              )
            }
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    {...register('first_name')}
                    error={errors.first_name?.message}
                    disabled={!isEditing}
                    required
                  />
                  <Input
                    label="Middle Name"
                    {...register('middle_name')}
                    error={errors.middle_name?.message}
                    disabled={!isEditing}
                  />
                  <Input
                    label="Last Name"
                    {...register('last_name')}
                    error={errors.last_name?.message}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Email Address"
                    type="email"
                    {...register('email')}
                    error={errors.email?.message}
                    disabled={!isEditing}
                    icon={<Mail className="h-4 w-4" />}
                    required
                  />
                  <Input
                    label="Phone Number"
                    type="tel"
                    {...register('phone')}
                    error={errors.phone?.message}
                    disabled={!isEditing}
                    icon={<Phone className="h-4 w-4" />}
                    required
                  />
                </div>
              </div>

              {/* Address Information */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </h4>
                <div className="space-y-4">
                  <Input
                    label="Street Address"
                    {...register('address_line1')}
                    error={errors.address_line1?.message}
                    disabled={!isEditing}
                    required
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="City"
                      {...register('city')}
                      error={errors.city?.message}
                      disabled={!isEditing}
                      required
                    />
                    <Input
                      label="State"
                      {...register('state')}
                      error={errors.state?.message}
                      disabled={!isEditing}
                      required
                    />
                    <Input
                      label="Country"
                      {...register('country')}
                      error={errors.country?.message}
                      disabled={!isEditing}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Employment
                </h4>
                <Input
                  label="Current Employer"
                  {...register('employer')}
                  error={errors.employer?.message}
                  disabled={!isEditing}
                  helperText="Optional: Your current employer or organization"
                />
              </div>

              {/* Non-editable Information Notice */}
              {!isEditing && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Some information like NIN, Date of Birth, and Sex cannot be changed online.
                      Please contact support if you need to update these fields.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};
